// ============================================================================
// 三端共享图标集（跨端规范 §2.6 / 2026-08-09 图标专项）
//
// 【为什么存在】UI 里还散着一批 emoji 与文本字形当图标用：⭐ 📧 🐦 ℹ️ ✕ ✓。
// 它们有一个共同的、无法绕开的缺陷 —— **emoji 是系统渲染的彩色字形，颜色不归
// 我们管**。商户把主色配成橙色，rating 卡就是"一颗黄星顶着五颗橙星"；暗色模式
// 下它们也不会跟着变。这不是审美问题，是"这块 UI 接不进主题系统"。文本 ✕/✓ 稍
// 好但同样不可控：字重、基线、在不同字体栈下的实际形状全看系统心情。
//
// 【为什么不引图标库】widget 是自包含 bundle，CSP 禁外链；任何图标库都会为了
// 六个图标带进整套字形。而我们**本来就有**这些形状 —— 散在各处的内联 SVG。
// 这个文件把它们提升成一份数据，三端读同一份。
//
// 【为什么是图元列表，不是 SVG path 字符串】
// path 的 `d` 语法要在 Kotlin 和 Swift 各写一个解析器（弧线、相对指令、隐式
// lineto…），那是三份可以各自出错的代码。图元（polyline / circle / rect / star）
// 直接对应 Canvas 与 SwiftUI 的绘图 API，任何一端都不需要解析器。web 侧的
// `d` 字符串由 `pathOf()` **从图元生成**，所以仍然只有一个真源 —— 不是"存两份
// 互相对照"，是"存一份、渲染时各自展开"。
//
// 【坐标系】统一 24×24 网格，与既有内联 SVG 一致（Android MediaGlyphs /
// RichGlyphs 的 `size.minDimension / 24f` 也是这个网格）。尺寸由调用点给，
// 图元按比例缩放。
//
// 【颜色纪律 —— 这才是重点】图标**自身不带颜色**。web 渲染成
// `stroke="currentColor"` / `fill="currentColor"`，颜色由调用点的 CSS `color`
// 决定，也就是必然走 --lc-* token；native 由调用点传 theme 里的颜色进来。
// 换成写死颜色的 SVG 等于没解决问题 —— 那只是把系统的黄换成了我们的黄。
//
// 【导出】`npm run tokens:export` 一并写出 sdk/shared/icon-set.json。
// ============================================================================

/** 网格边长。图元坐标都在 [0, GRID] 内。 */
export const ICON_GRID = 24;

/**
 * 图元类型：
 *   polyline  折线（开放）。`points: [[x,y], ...]`，描边。
 *   circle    圆。`cx, cy, r`；`fill: true` 时实心，否则描边。
 *   rect      圆角矩形。`x, y, w, h, rx`，描边。
 *   star      正 n 角星，实心。`cx, cy, outer, inner, points, rotateDeg`。
 *             参数化而不是展开成 10 个顶点：三端各自按同一个公式算，
 *             改一个半径不用重算十个数（Android StarGlyph 已经是这个算法，
 *             这里把它的参数抽出来当真源）。
 *
 * `strokeWidth` 是**网格单位**下的线宽，渲染时同样按比例缩放。
 */
export const ICONS = Object.freeze({
    /**
     * 关闭。既有内联 SVG `M18 6L6 18M6 6l12 12`（chat.js:1659），
     * Android ChatListView 的 24 格路径注释指的也是这一条。
     */
    close: Object.freeze({
        strokeWidth: 2,
        shapes: Object.freeze([
            Object.freeze({ type: 'polyline', points: Object.freeze([[18, 6], [6, 18]]) }),
            Object.freeze({ type: 'polyline', points: Object.freeze([[6, 6], [18, 18]]) }),
        ]),
    }),

    /**
     * 勾选。既有 `polyline 20 6 9 17 4 12`（widget.js 设置面板当前项）与
     * `M20 6L9 17l-5-5`（message-area.js）—— 同一条折线的两种写法，收敛成一条。
     */
    check: Object.freeze({
        strokeWidth: 2,
        shapes: Object.freeze([
            Object.freeze({ type: 'polyline', points: Object.freeze([[20, 6], [9, 17], [4, 12]]) }),
        ]),
    }),

    /**
     * 实心五角星（rating）。几何取 Android `StarGlyph` 现值，
     * 换掉的是 web 那边的 ★ U+2605 与 ⭐ U+2B50 —— 前者尚可（印刷符号，
     * 吃 currentColor），后者是 emoji，怎么都不会跟主色。
     */
    star: Object.freeze({
        shapes: Object.freeze([
            Object.freeze({ type: 'star', cx: 12, cy: 13, outer: 11, inner: 4.4, points: 5, rotateDeg: -90 }),
        ]),
    }),

    /** 信封。取 Android `MailGlyph` 现值（web notification-card 的同款）。 */
    mail: Object.freeze({
        strokeWidth: 1.8,
        shapes: Object.freeze([
            Object.freeze({ type: 'rect', x: 3, y: 5, w: 18, h: 14, rx: 2 }),
            Object.freeze({ type: 'polyline', points: Object.freeze([[3.5, 6.5], [12, 12.5], [20.5, 6.5]]) }),
        ]),
    }),

    /** 信息圈。取 widget.js 错误页图标现值（circle 9 + 竖线 + 点）。 */
    info: Object.freeze({
        strokeWidth: 1.6,
        shapes: Object.freeze([
            Object.freeze({ type: 'circle', cx: 12, cy: 12, r: 9 }),
            Object.freeze({ type: 'polyline', points: Object.freeze([[12, 8], [12, 13]]) }),
            Object.freeze({ type: 'circle', cx: 12, cy: 16, r: 1, fill: true }),
        ]),
    }),

    /**
     * 手柄（小游戏卡标题）。换掉 `🐦` —— 那只鸟既是 emoji，又把卡片钉死在
     * "Flappy Bird"这一款上；换成通用手柄，多一款游戏不用多一只动物。
     */
    game: Object.freeze({
        strokeWidth: 1.8,
        shapes: Object.freeze([
            Object.freeze({ type: 'rect', x: 2, y: 7, w: 20, h: 11, rx: 4 }),
            Object.freeze({ type: 'polyline', points: Object.freeze([[6, 12.5], [10, 12.5]]) }),
            Object.freeze({ type: 'polyline', points: Object.freeze([[8, 10.5], [8, 14.5]]) }),
            Object.freeze({ type: 'circle', cx: 16, cy: 11.5, r: 1.2, fill: true }),
            Object.freeze({ type: 'circle', cx: 18.5, cy: 14, r: 1.2, fill: true }),
        ]),
    }),
});

/** 正 n 角星 → 顶点列表。三端同一个公式（Android StarGlyph 的原式）。 */
export function starPoints({ cx, cy, outer, inner, points, rotateDeg }) {
    const out = [];
    for (let i = 0; i < points; i++) {
        const step = 360 / points;
        const ao = ((i * step + rotateDeg) * Math.PI) / 180;
        const ai = ((i * step + rotateDeg + step / 2) * Math.PI) / 180;
        out.push([cx + outer * Math.cos(ao), cy + outer * Math.sin(ao)]);
        out.push([cx + inner * Math.cos(ai), cy + inner * Math.sin(ai)]);
    }
    return out;
}

const r2 = (n) => Math.round(n * 100) / 100;

/**
 * 图元 → SVG path `d`。web 侧渲染用；**不是第二份真源**，是从图元展开出来的。
 * 只 polyline/star 走 path，circle/rect 用各自的 SVG 元素（更短也更准）。
 */
export function pathOf(shape) {
    if (shape.type === 'polyline') {
        return shape.points.map(([x, y], i) => `${i ? 'L' : 'M'}${r2(x)} ${r2(y)}`).join('');
    }
    if (shape.type === 'star') {
        return starPoints(shape).map(([x, y], i) => `${i ? 'L' : 'M'}${r2(x)} ${r2(y)}`).join('') + 'Z';
    }
    return '';
}

export const ICON_NAMES = Object.freeze(Object.keys(ICONS));
